/*
 *    lesson library utilities
 */

import deepClone from 'deep-clone';
import { v4 as uuidv4 } from 'uuid';
import { defaultNodeInits, typeDefMap, pageInits, elements } from '../defines/lessondefs';
import figma from './figma';
import axios from 'axios';
import { apiPaths, apiServer } from '@mirinae/shared/defines/paths';

// const deepCompare = require('deep-compare');
const objectDeepCompare = require('object-deep-compare');
const deepCompare = (a, b) => a && b && objectDeepCompare.CompareValuesWithConflicts(a, b).length === 0;

// --------- nodeTree hosuekeeping ----------------

export const lessonMap = {};
export const templateDocs = {};
let loadingTemplates = false;
export const templateFileKey = 'MdoUWvA7Prd3yR8uyxaXft';
export const templatePagesNodeID = '679:44';

export const allocateNodeIDs = (node, index) => {
    const walkTree = (node, parent, index) => {
        node.index = index !== undefined ? index : parent.children && parent.children.indexOf(node);
        if (node.fields) node.fields.forEach((f, i) => { f.index = i; });
        node.parent = parent;
        if (!node.lessonGuid) node.lessonGuid = parent.lessonGuid;
        if (!node.nodeID) {
            node.nodeID = lessonMap[node.lessonGuid].nextNodeID.toString();
            lessonMap[node.lessonGuid].nextNodeID += 1;
        }
        lessonNodeMap(node)[node.nodeID] = node;
        if (node.fields)
            node.fieldsByName = node.fields.reduce((fbn, f) => { fbn[f.name] = f; return fbn; }, {});
        if (node.children) node.children.forEach((c, i) => walkTree(c, node, i));
    };
    walkTree(node, node.parent, index);
};

// recursively wire any parent or child node references that are still nodeIDs
export const wireNodeMap = (nodeMap) => Object.values(nodeMap).forEach(node => {
    node.parent = nodeMap[node.parent] ? nodeMap[node.parent] : node.parent;
    if (node.children)
        node.children = node.children.map(c => nodeMap[c] ? nodeMap[c] : c);
});

// recursively wire any parent or child node references that are still nodeIDs in the given node-tree
export const wireNodeTree = (node, nodeMap) => {
    const deletedNodes = new Set();
    const changedNodes = new Set();
    const fixes = [];
    // console.log('wiring', node);
    const wireNode = (node, nodeMap) => {
        if (typeof node === 'string') {
            return null;
        } else {
            node.parent = nodeMap[node.parent] || node.parent;
            node.fieldsByName = node.fields ? node.fields.reduce((fbn, f) => { fbn[f.name] = f; return fbn; }, {}) : {};
            if (node.children) {
                const children = [];
                // console.log(`  children for ${node.nodeID}/${node.nodeClass}`, node.children);
                node.children.forEach(c => {
                    const cNode = wireNode(nodeMap[c] ? nodeMap[c] : c, nodeMap);
                    if (cNode) {
                        cNode.index = children.length;
                        children.push(cNode);
                    } else {
                        const cdescr = typeof c === 'string' ? c : `${c.nodeClass}/${c.title}(${c.nodeID})`;
                        const fix = `** dropping child ${cdescr} from node ${node.nodeClass}/${node.title}(${node.nodeID})`;
                        fixes.push(fix);
                        changedNodes.add(node);
                    }
                });
                // console.log(`  final children for ${node.nodeID}/${node.nodeClass}`, children);
                if (node.children.length === 0 || children.length > 0) {
                    node.children = children;
                    return node;
                } else {
                    fixes.push(`** dropping node ${node.nodeClass}/${node.title}(${node.nodeID}), lost all children`);
                    deletedNodes.add(node);
                    return null;
                }
            }
            return node;
        }
    };
    wireNode(node, nodeMap);
    if (fixes.length) console.log(fixes.join('\n'));
    return [fixes, changedNodes, deletedNodes];
};

export const consID = (guid, nodeID) => `${guid}|${nodeID}`;
export const globalNodeID = (node) => consID(node.lessonGuid, node.nodeID);
export const nodeFromGlobalID = (globalNodeID) => { const [lessonGuid, nodeID] = globalNodeID.split('|'); return lessonMap[lessonGuid].nodeMap[nodeID]; };
export const lessonNodeMap = (node) => lessonMap[node.lessonGuid].nodeMap;

// rebuild nodeMap from loaded DB lesson objects
export const rebuildNodeMap = (lessonObjects, lessonTreeRoot) => {
    // merge all lessonObject nodemaps into the global nodeMap
    const lessons = [];
    const updatedNodes = new Set();
    const deletedNodes = new Set();
    lessonObjects.forEach(lessonObject => {
        // repopulate UI side nodeMap
        if (!lessonObject.guid)
            lessonObject.guid = uuidv4();
        lessonMap[lessonObject.guid] = lessonObject;
        lessonObject.nodeMap[lessonObject.lessonNode.nodeID] = lessonObject.lessonNode; // point nodeMap[] lesson node at lessonObject.lessonNode to match player
        for (let nodeID in lessonObject.nodeMap) {
            const node = lessonObject.nodeMap[nodeID];
            if (node.nodeID === 'root')
                continue;
            node.lessonGuid = lessonObject.guid;
            lessonObject.nextNodeID = Math.max(lessonObject.nextNodeID || 1, Number(nodeID) + 1);
            if (node.nodeClass === 'lesson')
                lessons.push(node);
            if (node.fixed) {
                // detect fixes registered by player wireNodeTree initial loading & save them todo: should report them to editor!
                delete node.fixed;
                updatedNodes.add(node);
            }
            // ad-hoc node fix-ups for existing lessons
            lessonFixUps(lessonObject.nodeMap, node, updatedNodes);
        }
        lessonObject.nodeMap['root'] = lessonTreeRoot;
        lessonObject.nextNodeID += 10; // buffer
        // wire up id-based refs
        const [fixes, changedNodes, droppedNodes] = wireNodeTree(lessonObject.lessonNode, lessonObject.nodeMap);
        changedNodes.forEach(n => updatedNodes.add(n));
        droppedNodes.forEach(n => deletedNodes.add(n));
    });
    const lessonOrder = (l) => (l.number || 1000) + (l.backupForNodeID || l.status === 'Backup' ? 1000 : 0);
    lessons.sort((l1, l2) => lessonOrder(l1) - lessonOrder(l2)).forEach((lesson, i) => lesson.index = i);
    return [lessons, updatedNodes, deletedNodes];
};

// apply ad-hoc fixups to lesson node tree on loading into editor
//   these will eventually be deprecated as all old lessons are repaired
export const lessonFixUps = (nodeMap, node, updatedNodes, addedNodes = []) => {
    const _n = (nodeID) => typeof nodeID === 'string' ? nodeMap[nodeID] : nodeID;
    // add layout field to all the pattern animation elements
    if (node.type.match(/patternAnimation|introductoryVerbConjugation|verbConjugation|introductoryAuxiliaryVerb|sentenceForm|sentenceFormExample/)) {
        if (!fieldsByName(node, 'layout')) {
            node.fields.splice(0, 0, { name: 'layout', type: 'choice', label: 'Layout', choices: ['Horizontal', 'Vertical', 'Single row'], value: 'Horizontal' });
            updatedNodes.add(node);
        }
        if (!node.fieldEdits || !node.fieldEdits.includes('searchAndReplace')) {
            node.fieldEdits = ['duplicate', 'move', 'delete', 'rename', 'hide', 'searchAndReplace'];
            updatedNodes.add(node);
        }
        node.fields.filter(f => f.parts).forEach(f => ['koreanParts', 'englishParts'].forEach(sp => f.parts[sp] && f.parts[sp].forEach(w => {
            if (w.color === 'separator') {
                w.color = 'separate';
                updatedNodes.add(node);
            }
        })));
    } else if (node.type.match(/miniLessonTableCard|patternVariantsSummary|patternSummary|phraseBoard/)) {
        // patch various types that have out-of-date field defs
        const fieldDefs = elements[node.componentName].init.fields;
        if (node.componentName === 'miniLessonTableCard' && node.fields.length === 5) {
            node.fields.splice(3, 0, deepClone(fieldDefs[3])); // insert new annotation field (I wish there were a cleaner update hack!)
            node.fields.forEach((f, i) => f.index = i);
            updatedNodes.add(node);
        }
        node.fields.forEach((f, i) => {
            if (fieldDefs[i].name !== f.name) {
                if (node.type === 'patternVariantsSummary') {
                    if (i === 3 && node.fields[i].name !== 'variants') {
                        node.fields[i].name = 'variants'; // hack because of deleted followedBy field!!
                        updatedNodes.add(node);
                    }
                } else {
                    console.log('** field name mismatch', node, fieldDefs[i], f.name);
                    f.name = fieldDefs[i].name;
                    updatedNodes.add(node);
                }
            }
        });
        if (node.type === 'patternVariantsSummary' && node.fields.length === 4 && node.fields[3].name !== 'variants') { // frick!!
            node.fields[3].name = 'variants'; // hack because of deleted followedBy field
            updatedNodes.add(node);
        }
    } else if (node.type === 'cultureExample' && node.fields[3] && node.fields[3].name !== 'postfix') {
        node.fields[3].name = 'postfix';
        node.fields[0].choices = ['Song', 'Film & Video'];
        updatedNodes.add(node);
    // } else if (node.nodeClass === 'page' && node.componentName === 'CulturePattern' && node.children[0].children.length === 5 && node.children[0].children[3].name === 'example1') {
    //     node.children[0][3].name = 'example';
    //     updatedNodes.add(node.children[0].children[3]);
    } else if (node.type.match(/letterCard|wordCard|batchimCard|makeTheWord|pickALetter/)) {
        if (!fieldsByName(node, 'voicing')) {
            node.fields.push({ name: 'voicing', type: 'choice', label: 'Voicing', choices: ['Male slow', 'Male normal', 'Male fast', 'Female slow', 'Female normal', 'Female fast'], value: 'Male slow' });
            updatedNodes.add(node);
        } else if (fieldsByName(node, 'voicing').choices.length < 6) {
            fieldsByName(node, 'voicing').choices = ['Male slow', 'Male normal', 'Male fast', 'Female slow', 'Female normal', 'Female fast'];
            updatedNodes.add(node);
        }
    } else if (node.type === 'textCard') {
        const f = fieldsByName(node, 'title') || node.fields.find(f => f.label === 'Title' && f.name === 'text');
        if (f) {
            f.name = 'cardTitle';
            updatedNodes.add(node);
        }
    } else if (node.type === 'matchTheLetters') {
        if (!node.exercise) {
            node.exercise = true;
            updatedNodes.add(node);
        }
    } else if (node.nodeClass === 'page' && node.componentName.match(/MeetTheLetters|MeetTheWords|MeetTheBatchim|PickTheLetter|MakeTheWord|MatchTheLetters/)) {
        if (node.background !== 'Hangul') {
            node.background = 'Hangul';
            updatedNodes.add(node)
        }
    } else if (node.nodeClass === 'page' && node.componentName.match(/LessonStart/)) {
        if (node.background === 'IntroToHangul') {
            node.background = 'Hangul start';
            updatedNodes.add(node)
        }
    } else if (node.nodeClass === 'page' && node.componentName.match(/LessonEnd/)) {
        if (node.background !== 'Hangul end') {
            node.background = 'Hangul end';
            updatedNodes.add(node)
        }
    } else if (node.nodeClass === 'page' && node.componentName.match(/StarterImageAndText|ImageAndTextStart/)) {
        if (node.background !== 'ImageAndText') {
            node.background = 'ImageAndText';
            updatedNodes.add(node)
        }
        if (node.showLogo !== true) {
            node.showLogo = true;
            updatedNodes.add(node)
        }
    }
    if (node.nodeClass === 'element' && node.type.match(/irregularIcon|miniLessonCard/)) {
        const iconField = node.fields.find(f => f.name === 'icon');
        if (iconField && iconField.choices.length !== 7) {
            iconField.choices = ['None', 'ㅡ drops', 'ㅅ irregular', 'ㄹ irregular', 'ㅂ irregular', 'ㄷ irregular', 'ㅎ irregular',];
            updatedNodes.add(node);
        }
    }
    if (node.nodeClass === 'element' && node.name === '-- untitled --' && node.title) {
        node.name = node.title;
        updatedNodes.add(node);
    }
    // ensure page nodes have all their inits
    if (node.nodeClass === 'page' && pageInits[node.componentName]) {
        Object.getOwnPropertyNames(pageInits[node.componentName]).filter(p => !node[p]).forEach(p => {
            node[p] = pageInits[node.componentName][p];
            updatedNodes.add(node);
        });
    }

    const makeLayoutBox = (name,  step, additions) => ({
            name,
            type: "layoutBox",
            modes: [ "step", "autoplay" ],
            index: step.children.length,
            lessonGuid: node.lessonGuid,
            parent: step.nodeID,
            nodeClass: "element",
            size: "100%",
            componentName: "layoutBox",
            title: name,
            nodeID: (Math.max(...Object.getOwnPropertyNames(nodeMap).map(n => Number(n))) + 10).toString(),
            ...additions,
            children: []
        });

    // fix card element names on irregularMiniLess nav pages
    if (node.nodeClass === 'page' && node.componentName === 'IrregularMiniLessonNav') {
        const step = node.children && _n(node.children[0]);
        const cards = step && step.children.filter(e => _n(e).type === 'miniLessonCard').map(e => _n(e));
        cards.sort((c1, c2) => c1.geom.x - c2.geom.x).forEach((c, i) => {
            const name = `miniLessonCard${i + 1}`;
            if (c.name !== name) {
                c.name = name;
                updatedNodes.add(node);
            }
        });
        // add missing layout box
        if (!step.children.find(c => _n(c).name === 'buttonLayout')) {
            const buttonLayout = makeLayoutBox("buttonLayout", step, {
                geom: { x: 80, y: 328, width: 1040, height: 250 },
                constraints: { vertical: "TOP", horizontal: "LEFT_RIGHT" },
                mobile: { geom: { x: 30, y: 245, width: 330, height: 99 },
                    constraints: { vertical: "TOP", horizontal: "LEFT_RIGHT" }
                },
            });
            step.children.push(buttonLayout.nodeID);
            nodeMap[buttonLayout.nodeID] = buttonLayout;
            updatedNodes.add(node);
            addedNodes.push(buttonLayout);
        }
    }

    if (node.nodeClass === 'page' && node.componentName === 'LessonGoalWithImage') {
        const step = node.children && _n(node.children[0]);
        // add missing layout box
        if (!step.children.find(c => _n(c).name === 'sentencesBox')) {
            const sentencesLayout = makeLayoutBox("sentencesBox", step, {
                geom: { x: 607, y: 288, width: 393, height: 280 },
                constraints: { vertical: "TOP", horizontal: "LEFT_RIGHT" },
                mobile: { geom: { x: 30, y: 506, width: 330, height: 280 },
                    constraints: { vertical: "TOP", horizontal: "LEFT_RIGHT" }
                },
            });
            step.children.push(sentencesLayout.nodeID);
            nodeMap[sentencesLayout.nodeID] = sentencesLayout;
            updatedNodes.add(node);
            addedNodes.push(sentencesLayout);
        }
    }

    // ensure fields indexed correctly
    if (node.nodeClass === 'element' && node.fields)
        node.fields.forEach((f, i) => { if (f.index !== i) { updatedNodes.add(node); f.index = i; }});
}

// ------------ nodeTree surgery ------------------

export const insertNewNode = (nodeClass, typeDef, refNode, offset, selectionContext) => {
    // general tree node-insertion helper
    const parent = refNode.parent;
    const newNode = { ...(deepClone(defaultNodeInits[nodeClass])), ...(typeDef && deepClone(typeDef.init) || {}) };
    newNode.nodeClass = nodeClass;
    newNode.parent = parent;
    allocateNodeIDs(newNode);

    if (!parent.children)
        parent.children = [];
    if (refNode) {
        const index = parent.children.indexOf(refNode);
        newNode.index = index + offset;
        parent.children.splice(newNode.index, 0, newNode);
    } else {
        newNode.index = parent.children.length;
        parent.children.push(newNode);
    }
    parent.children.forEach((c, i) => c.index = i);
    if (!selectionContext.expandedNodes.includes(globalNodeID(parent)))
        selectionContext.expandedNodes.push(globalNodeID(parent));

    selectionContext.update({ selectedNode: newNode, expandedNodes: selectionContext.expandedNodes });

    return newNode;
};

export const buildNode = (nodeClass, typeDef, parent, nodeToUpdate) => {
    // build out node tree for given node-class & type-def
    // gather ordered list of figma component instancing needed so it can be sent in one message - individual messages are async and can arrive out of order!
    const nodesToSave = new Set([parent]);
    const node = buildLessonTree(nodeClass, typeDef, parent, nodeToUpdate, nodesToSave);
    conformToFigmaLayout(node, nodesToSave);
    return [nodesToSave, node];
};

const buildLessonTree = (nodeClass, typeDef, parent, nodeToUpdate, nodesToSave) => {
    // build out node's default contents from the lessonType defs
    let node = nodeToUpdate;
    if (!node) {
        // create new node if brand new
        node = { ...(deepClone(defaultNodeInits[nodeClass])), ...(deepClone(typeDef.init) || {}) };
        if (!parent.children)
            parent.children = [];
        if (nodeClass === 'lesson') {
            // make a dummy lessonObject for now holding just fields required for initial build, will be replaced when DB responds with actuall object
            node.lessonGuid = uuidv4();
            node.nodeID = "0";
            lessonMap[node.lessonGuid] = { guid: node.lessonGuid, nextNodeID: 1, nodeMap: {} };
            // replace current active lesson
            parent.children = [node];
        } else {
            node.index = parent.children.length;
            node.lessonGuid = parent.lessonGuid;
            parent.children.push(node);
        }
    }

    // build out node's default contents
    node.parent = parent;
    node.type = typeDef.type;
    node.nodeClass = nodeClass;
    node.typeDefPathName = typeDef.pathName;
    const lesson = ancestorsFor(node).lesson;
    if (lesson) {
        node.lessonFigmaNodeID = lesson.figmaNodeID;
        console.log('lessonFigmaNodeID', node, lesson.figmaNodeID);
    }
    if (typeDef.customFigmaNodeID) node.customFigmaNodeID = typeDef.customFigmaNodeID;
    if (typeDef.name) node.name = typeDef.name;
    if (typeDef.exercise) node.exercise = typeDef.exercise;
    if (typeDef.managedByPage) node.managedByPage = typeDef.managedByPage;
    if (typeDef.size) node.size = typeDef.size;
    if (typeDef.componentName || nodeClass === 'element') node.componentName = typeDef.componentName || typeDef.type;
    if (!node.title || node.title === '-- untitled --')
        node.title = typeDef.name || typeDef.type
    node.title = node.title.replace(/-p\d$/, ''); // trim type-number gunk

    allocateNodeIDs(node);

    const nodeBuilders = {
        lesson: () => {
            node.children = typeDefMap[node.typeDefPathName].sections.map(s => buildLessonTree('section', s, node, null, nodesToSave));
            nodesToSave.add(node);
        },
        section: () => {
            node.children = typeDefMap[node.typeDefPathName].pages.filter(p => !p.dontInstance).map(p => buildLessonTree('page', p, node, null, nodesToSave));
            nodesToSave.add(node);
        },
        page: () => {
            node.children = typeDefMap[node.typeDefPathName].steps.map(e => buildLessonTree('step', e, node, null, nodesToSave));
            nodesToSave.add(node);
        },
        step: () => {
            node.children = typeDefMap[node.typeDefPathName].elements.map(e => buildLessonTree('element', e, node, null, nodesToSave));
            nodesToSave.add(node);
        },
        element: () => {
            nodesToSave.add(node);
        },
    };
    // recursively build node from lesson-def entries
    nodeBuilders[nodeClass]();
    allocateNodeIDs(node);

    // console.log('buildLessonTree ->', nodeClass, node);

    return node;
};

// find template instances in the loaded Figma JSON docs
export const findTemplateInstances = (node, page) => {
    const templatePage = templateDocs.templatePages[page.componentName];
    const mobileTemplatePage = templateDocs.templatePages[`${page.componentName}_m`];
    const instance = templatePage && (templatePage.nodesByName[node.name] || templatePage.nodesByName[`${node.name}Text`]);
    const mobileInstance = mobileTemplatePage && (mobileTemplatePage.nodesByName[node.name] || (mobileTemplatePage.nodesByName[`${node.name}Text`]));
    return [instance, mobileInstance];
};

// breadth first search for named descendant in template struct
const findChild = (templateNode, name) => {
    let check = [...templateNode.children];
    while (check.length > 0) {
        const found = check.splice(0).find(c => {
            if (c.name === name)
                return c;
            if (c.children)
                check = check.concat(c.children);
        });
        if (found) return found;
    }
};

export const getTextFieldStyles = (figmaField) => ({
    fontName: { "family" : figmaField.style.fontFamily, "style" : figmaField.style.italic ? 'italic' : 'normal' },
    fontSize: figmaField.style.fontSize,
    fontWeight: figmaField.style.fontWeight,
    color: figmaField.fills || [{ "type" : "SOLID", "color" : { "r" : 0.235, "g" : 0.235, "b" : 0.235 }}],
    textAlignHorizontal: figmaField.style.textAlignHorizontal,
    textAlignVertical: figmaField.style.textAlignVertical,
    textAutoResize: figmaField.style.textAutoResize,
    geom: { width: figmaField.absoluteBoundingBox.width, height: figmaField.absoluteBoundingBox.height },
});

// update node tree to conform geom & layout to current figma templates
export const conformToFigmaLayout = (node, nodesToSave) => {
    const conformNode = (node) => {
        if (node.nodeClass === 'element') {
            // element geometry loading from Figma template instances
            // check template page instance against lesson node, make esson nodes follow template instance
            const { page } = ancestorsFor(node);
            const [pageInstance, mobilePageInstance] = findTemplateInstances(node, page);
            const instanceCount = node.parent.children.filter(n => n.name === node.name).length;
            if (pageInstance && instanceCount === 1) {
                const geom = pageInstance && {
                    x: pageInstance.x,
                    y: pageInstance.y,
                    width: pageInstance.width,
                    height: pageInstance.height,
                };
                const constraints = pageInstance && { ...pageInstance.constraints };
                // check for changes
                if (!deepCompare(node.geom, geom)) {
                    node.geom = geom;
                    nodesToSave.add(node);
                }
                if (!deepCompare(node.constraints, constraints)) {
                    node.constraints = constraints;
                    nodesToSave.add(node);
                }
                // now the (text) fields
                if (node.fields)
                    node.fields.forEach(f => {
                        let figmaTemplateNode = pageInstance && findChild(pageInstance, f.name);
                        if (!figmaTemplateNode && f.type === 'text' && f.name !== 'text' && !f.name.match(/scale|cardTitle/)) {
                            figmaTemplateNode = pageInstance && findChild(pageInstance, 'text');
                            if (figmaTemplateNode) {
                                f.name = 'text';
                                nodesToSave.add(node);
                            }
                        }
                        if (f.type === 'text' && figmaTemplateNode && figmaTemplateNode.type === 'TEXT') {
                            const templateTextStyle = getTextFieldStyles(figmaTemplateNode);
                            if (!deepCompare(templateTextStyle, f.textStyle)) {
                                f.textStyle = templateTextStyle;
                                nodesToSave.add(node);
                            }
                        } else if (figmaTemplateNode && f.type === 'parts') {
                            const tko = findChild(figmaTemplateNode, 'koreanParts');
                            if (tko && tko.children && tko.children[0].type === 'TEXT') {
                                const tkoFontSize = tko.children[0].style.fontSize;
                                if (!(f.style && f.style.koreanFontSize === tkoFontSize)) {
                                    f.style = { ...(f.style || {}), koreanFontSize: tkoFontSize };
                                    nodesToSave.add(node);
                                }
                            }
                            const teo =  findChild(figmaTemplateNode, 'englishParts');
                            if (teo && teo.children && teo.children[0].type === 'TEXT') {
                                const teoFontSize = teo.children[0].style.fontSize;
                                if (!(f.style && f.style.englishFontSize === teoFontSize)) {
                                    f.style = { ...(f.style || {}), englishFontSize: teoFontSize };
                                    nodesToSave.add(node);
                                }
                            }
                        }
                    });
            }

            if (mobilePageInstance) {
                const mobileGeom = mobilePageInstance && {
                    x: mobilePageInstance.x,
                    y: mobilePageInstance.y,
                    width: mobilePageInstance.width,
                    height: mobilePageInstance.height,
                };
                const mobileConstraints = mobilePageInstance && { ...mobilePageInstance.constraints };
                if (mobileGeom && !deepCompare(node.mobile && node.mobile.geom, mobileGeom)) {
                    node.mobile = node.mobile || {};
                    node.mobile.geom = mobileGeom;
                    nodesToSave.add(node);
                }
                if (mobileConstraints && !deepCompare(node.mobile && node.mobile.constraints, mobileConstraints)) {
                    node.mobile = node.mobile || {};
                    node.mobile.constraints = mobileConstraints;
                    nodesToSave.add(node);
                }
                if (node.fields)
                    node.fields.forEach(f => {
                        const figmaMobileTemplateNode = mobilePageInstance && findChild(mobilePageInstance, f.name);
                        if (figmaMobileTemplateNode && figmaMobileTemplateNode.type === 'TEXT') {
                            const templateTextStyle = getTextFieldStyles(figmaMobileTemplateNode);
                            if (!deepCompare(templateTextStyle, f.mobile && f.mobile.textStyle)) {
                                f.mobile = { ...(f.mobile || {}), textStyle: templateTextStyle };
                                nodesToSave.add(node);
                            }
                        } else if (figmaMobileTemplateNode && f.type === 'parts') {
                            const tko = findChild(figmaMobileTemplateNode, 'koreanParts');
                            if (tko && tko.children && tko.children[0].type === 'TEXT') {
                                const tkoFontSize = tko.children[0].style.fontSize;
                                if (!(f.mobile && f.mobile.style && f.mobile.style.koreanFontSize === tkoFontSize)) {
                                    f.mobile = { ...(f.mobile || {}), style: { ...(f.mobile && f.mobile.style || {}), koreanFontSize: tkoFontSize } };
                                    nodesToSave.add(node);
                                }
                            }
                            const teo = findChild(figmaMobileTemplateNode, 'englishParts');
                            if (teo && teo.children && teo.children[0].type === 'TEXT') {
                                const teoFontSize = teo.children[0].style.fontSize;
                                if (!(f.mobile && f.mobile.style && f.mobile.style.englishFontSize === teoFontSize)) {
                                    f.mobile = { ...(f.mobile || {}), style: { ...(f.mobile && f.mobile.style || {}), englishFontSize: teoFontSize } };
                                    nodesToSave.add(node);
                                }
                            }
                        }
                    });
            }
        } else {
            // renamed elements
            if (node.nodeClass === 'step') {
                if (node.parent.componentName === 'MiniLessonTable' && node.children[0].name !== 'callToAction') {
                    node.children[0].name = 'callToAction';
                    node.children[1].name = 'directions';
                    nodesToSave.add(node);
                }
                // // now ensure presence of layoutBox types from current typeDefMap defs
                // const typeDef = typeDefMap[node.typeDefPathName] || (typeDefMap[pageTypeDefs[node.parent.componentName] && pageTypeDefs[node.parent.componentName].steps[0].pathName]);
                // if (typeDef)
                //     typeDef.elements.filter(tde => tde.componentName === 'layoutBox').forEach(tde => {
                //         if (!(node.children && node.children.find(e => e.componentName === 'layoutBox' && e.name === tde.name))) {
                //             // missing layout, add it
                //             addElement(tde, node);
                //         }
                //     });
            }
            if (node.nodeClass === 'page') {
                const templatePage = templateDocs.templatePages[node.componentName];
                const mobileTemplatePage = templateDocs.templatePages[`${node.componentName}_m`];
                if (templatePage && !node.lessonBounds) {
                    const lessonBounds = findChild(templatePage, 'lessonBounds');
                    if (lessonBounds) {
                        node.lessonBounds = {
                            x: lessonBounds.x,
                            y: lessonBounds.y,
                            width: lessonBounds.width,
                            height: lessonBounds.height,
                        };
                        nodesToSave.add(node);
                    }
                }
                if (mobileTemplatePage && (!node.mobile || !node.mobile.lessonBounds)) {
                    const mobileLessonBounds = findChild(mobileTemplatePage, 'lessonBounds');
                    if (mobileLessonBounds) {
                        node.mobile = { ...(node.mobile || {}),
                            lessonBounds: {
                                x: mobileLessonBounds.x,
                                y: mobileLessonBounds.y,
                                width: mobileLessonBounds.width,
                                height: mobileLessonBounds.height,
                            }
                        };
                        nodesToSave.add(node);
                    }
                }
            }
            // recurse
            if (node.children)
                node.children.forEach(c => conformNode(c));
        }
    };

    if (node)
        conformNode(node);
    else
        Object.values(nodeMap).filter(n => n.nodeClass === 'lesson').forEach(n => conformNode(n));
};

// checks for lesson-tree consistency
export const checkAndRepairLessonTree = (lesson) => {
    const fixes = [];
    const changedNodes = new Set();
    const deletedNodes = new Set();
    const checkNode = (node, parent) => {
        //  ensure LessonStart pages have the
        const { lesson } = ancestorsFor(node);
        if (lesson && lesson.lessonGuid !== node.lessonGuid) {
            // node in wrong lesson!  remove
            fixes.push(`node in wrong lesson ${node.nodeID} - ${lesson.lessonGuid} : ${node.lessonGuid}, removing`);
            console.log(`node in wrong lesson ${node.nodeID} - ${lesson.lessonGuid} : ${node.lessonGuid}, removing`);
            deletedNodes.add(node);
        }
        if (parent && node.parent !== parent) {
            // parent-child inconsistent, repair in favor of parent
            fixes.push(`node parent wrong ${node.parent.nodeID} - ${parent.nodeID}, forced to ${parent.nodeID}`);
            console.log(`node parent wrong ${node.parent.nodeID} - ${parent.nodeID}, forced to ${parent.nodeID}`);
            node.parent = parent;
            changedNodes.add(node);
        }
        if (parent && !parent.children.find(c => c === node)) {
            // not in my parent's children, drop me
            fixes.push(`not in my parent's children ${parent.children} - ${parent.nodeID}, removing ${node.nodeID}`);
            console.log(`not in my parent's children ${parent.children} - ${parent.nodeID}, removing ${node.nodeID}`);
            // node.parent = parent;
            // changedNodes.add(node);
        }
        if (node.fields)
            node.fields.filter(f => f.src).forEach(f => {
                // ensure fieldRefs exist for any field sources
                const [obj, prop] = f.src.split('.');
                const srcObj = ancestorsFor(node)[obj];
                if (!srcObj.fieldRefs || !srcObj.fieldRefs.find(fr => fr.nodeID === node.nodeID && fr.fieldName === f.name)) {
                    fixes.push(`missing fieldRef in ${node.nodeID} - ${f.name}, restoring`);
                    console.log(`missing fieldRef in ${node.nodeID} - ${f.name}, restoring`);
                    srcObj.fieldRefs = (srcObj.fieldRefs || []).concat({
                        nodeID: node.nodeID,
                        fieldName: f.name,
                    });
                 changedNodes.add(srcObj);
               }
            });
        const children = [];
        node.children && node.children.forEach((c, i) => {
            if (typeof c === 'string') {
                // unknown child!
                fixes.push(`Unknown child in ${node.nodeID} - ${c}, removing`);
                console.log(`Unknown child in ${node.nodeID} - ${c}, removing`);
                changedNodes.add(node);
            } else {
                children.push(c);
            }
        });
        node.children = children;
        node.children && node.children.forEach((c, i) => {
            if (c.index !== i) {
                // index out of order!
                fixes.push(`node index out of order in ${node.nodeID}, ${node.index} - ${i}, forced to ${i}`);
                console.log(`node index out of order in ${node.nodeID}, ${node.index} - ${i}, forced to ${i}`);
                c.index = i;
                changedNodes.add(c);
            }
            checkNode(c, node);
        });
        // // ad-hoc fixups (on the way out of the recursion)
        // //   instance 'footer' layoutBox in LessonStart pages if not present   (do by hand for now, too tricky in the middle of other fixups here)
        // if (node.nodeClass === 'page' && componentName === 'LessoStart') {
        //     if (!(node.children[0].children ** node.children[0].children.find(e => e.name === 'footer'))) {
        //         addElement.bind(null, typeDef)
        //     }
        // }
    };
    checkNode(lesson);
    // reconform
    conformToFigmaLayout(lesson, changedNodes);

    return [
        fixes,
        [...changedNodes].filter(n => n.nodeID !== 'root').map(n => ({ ...deflateNode(n) })),
        [...deletedNodes].filter(n => n.nodeID !== 'root').map(n => ({ ...deflateNode(n) }))
    ];
};


// duplicate given node as a following sibling
export const duplicateNode = (node, copyUpdates, focusOnOriginal, toParent, toIndex) => {
    const nodesToSave = new Set();
    const freshenNodeTree = (newNode, parent) => {
        // clean clone to prepare for re-instancing on the figma side
        newNode.parent = parent;
        if (newNode.nodeClass === 'lesson') {
            // make a dummy lessonObject for now holding just fields required for initial build, will be replaced when DB responds with actuall object
            newNode.lessonGuid = uuidv4();
            lessonMap[newNode.lessonGuid] = { guid: newNode.lessonGuid, nextNodeID: 1, nodeMap: {} };
            newNode.nodeID = "0";
            lessonNodeMap(newNode)[newNode.nodeID] = newNode;
        } else {
            newNode.lessonGuid = parent.lessonGuid;
            delete newNode['figmaNodeID']; delete newNode['lessonFigmaNodeID']; delete newNode['figmaKey'];
            if (newNode.fields)
                newNode.fields.forEach(f => { delete f['figmaNodeID']; });
            newNode.nodeID = lessonMap[newNode.lessonGuid].nextNodeID.toString(); lessonMap[newNode.lessonGuid].nextNodeID += 1;
            lessonNodeMap(newNode)[newNode.nodeID] = newNode;
            if (newNode.fields)
                newNode.fieldsByName = newNode.fields.reduce((fbn, f) => { fbn[f.name] = f; return fbn; }, {});
        }
        newNode.children = newNode.children && newNode.children.map(nc => freshenNodeTree(nc, newNode));
        nodesToSave.add(newNode);
        return newNode;
    };
    // insert new duplicate in toParent at toIndex if supplied, else after source node & re-index
    const parent = toParent || node.parent;
    const destIndex = toIndex !== undefined ? toIndex : node.index + 1;
    const newNode = deepClone(node);
    Object.assign(newNode, copyUpdates);
    freshenNodeTree(newNode, parent);
    if (newNode.nodeClass === 'lesson') {
        // replace current active lesson
        parent.children = [newNode];
    } else {
        const siblings = [...parent.children];
        siblings.splice(destIndex, 0, newNode);
        siblings.forEach((c, i) => { c.index = i; nodesToSave.add(c); });
        parent.children = [...siblings];
        nodesToSave.add(parent);
    }
    //
    return [newNode, nodesToSave];
};

export const moveNode = (movingNode, toNode, place) => {
    // move a node
    let changedNodes = new Set([movingNode]);
    // check if differing lessons
    const fromLesson = ancestorsFor(movingNode).lesson;
    const toLesson = ancestorsFor(toNode).lesson;
    if (fromLesson !== toLesson) {
        // duplicate into dest lesson, remove from source lesson
        const toIndex = toNode.index + (place === 'below' ? 1 : 0);
        const toParent = toNode.parent;
        const [newNode, nodesToSave] = duplicateNode(movingNode, {}, false, toParent, toIndex);
        changedNodes.add(nodesToSave);
    } else {
        // insert into destination side
        const toParent = toNode.parent;
        const fromParent = movingNode.parent;
        const toNodeSiblings = toParent.children;
        const movingNodeSiblings = fromParent.children;
        const destIndex = toNode.index + (place === 'below' ? 1 : 0);
        toNodeSiblings.splice(destIndex, 0, movingNode);
        // remove from moving side
        const srcIndex = fromParent === toParent && movingNode.index > toNode.index ? movingNode.index + 1 : movingNode.index;
        movingNodeSiblings.splice(srcIndex, 1);
        // renumber
        toParent.children = [...toNodeSiblings];
        toParent.children.forEach((c, i) => { c.index = i; });
        changedNodes.add(toParent); toParent.children.forEach(c => changedNodes.add(c));
        if (fromParent !== toParent) {
            // shift parent, clean that up
            fromParent.children = [...movingNodeSiblings];
            fromParent.children.forEach((c, i) => { c.index = i; });
            movingNode.parent = toParent;
            changedNodes.add(fromParent); fromParent.children.forEach(c => changedNodes.add(c));
        }
        // tell the figma side
        figma.call('nodeMoved', { node: deflateNode(movingNode), fromParentNode: deflateNode(fromParent), toParentNode: deflateNode(toParent) });
    }
    //
    return [...changedNodes];
};

// delete node tree under node
export const removeNode = (node) => {
    const changedNodes = [];
    const deletedNodes = [];
    if (typeof node.parent !== 'string') {
        const parent = node.parent;
        changedNodes.push(parent);
        // slice node out of sibling list
        const nodeSiblings = parent.children;
        nodeSiblings.splice(node.index, 1);
        nodeSiblings.forEach((c, i) => { c.index = i; changedNodes.push(c); });
        parent.children = [...nodeSiblings];
    }
    // gather deleted nodes
    const gather = (n) => {
        deletedNodes.push(n);
        n.children && n.children.forEach(c => gather(c));
    };
    gather(node);
    // clean up nodeMap
    deletedNodes.forEach(dn => delete lessonNodeMap(dn)[dn.nodeID]);
    // update figma side
    figma.call('nodeDeleted', { nodeID: node.nodeID, deletedNodeIDs: deletedNodes.map(dn => dn.nodeID) });

    return [changedNodes, deletedNodes];
};

// ------ utility functions ------

export const parentRefsToNodeIDs = (node) => {
    node.parent = node.parent.nodeID;
    node.children && node.children.forEach(c => parentRefsToNodeIDs(c));
    return node;
};

export const nodeIDsToParentRefs = (node) => {
    node.parent = lessonNodeMap(node)[node.parent];
    node.children && node.children.forEach(c => nodeIDsToParentRefs(c));
    return node;
};

export const lessonTreeToJSON = (lessonTree) => {
    parentRefsToNodeIDs(lessonTree);
    const json = JSON.stringify(lessonTree);
    nodeIDsToParentRefs(lessonTree);
    return json;
};

export const ancestorsFor = (node) => {
    const ancestors = { };
    ancestors[node.nodeClass] = node;
    let parent = node.parent;
    while (parent && parent.nodeID !== 'root' ) {
        ancestors[parent.nodeClass] = parent;
        parent = parent.parent;
    }
    return ancestors;
};

export const typeDefFor = (parent, type) => {
    const key = `${parent.typeDefPathName}/${type}`;
    return typeDefMap[parent.nodeID === 'root' ? type : `${parent.typeDefPathName}/${type}`] || typeDefMap[type];
};

export const deflateNode = (node) => {
    const deflatedNode = {
        ...node,
        parent: typeof node.parent === 'string' ? node.parent : node.parent && node.parent.nodeID,
        children: node.children ? node.children.map(c => typeof c === 'string' ? c : c.nodeID) : []
    };
    if (deflatedNode.fields)
        deflatedNode.fields = deflatedNode.fields.map(f => { delete f.ref; return { ...f }; });
    delete deflatedNode.layout;
    delete deflatedNode.fieldsByName;
    return deflatedNode;
};
export const inflateNode = (node, nodeMap) => ({ ...node, parent: nodeMap[node.parent], children: node.children ? node.children.map(c => nodeMap[c]) : [] });
export const deflateNodeWithFigmaIDs = (node) => ({ ...node, parent: node.parent && node.parent.figmaNodeID, children: node.children ? node.children.map(c => c.figmaNodeID) : [] });

export const deflateNodeTree = (node) => {
    const nodeStruct = { root: node.nodeID, nodeMap: {}, timestamp: Date.now() };
    const deflateTree = (n) => {
        if (n.children) n.children.forEach(c => deflateTree(c));
        if (n.nodeClass !== 'root')
            nodeStruct.nodeMap[n.nodeID] = deflateNode(n);
    };
    deflateTree(node);
    return nodeStruct;
};

export const nodeTreeToJSON = (node) => {
    return JSON.stringify(deflateNodeTree(node));
};

export const lessonFor = (node) => node && (node.nodeClass === 'lesson' ? node : lessonFor(node.parent));
export const pageFor = (node) => node && (node.nodeClass === 'page' ? node : pageFor(node.parent));

export const fieldsByName = (node, name) => node.fields.find(f => f.name === name);

export const getFigmaTemplates = async (refresh) => {
    // load template file data (delivered as a gzip JSON file)

    console.log('getFigmaTemplates()');
    // LessonEditorDev:template/elements: https://www.figma.com/file/MdoUWvA7Prd3yR8uyxaXft/?node-id=678%3A0
    // LessonEditorDev:template/pages: https://www.figma.com/file/MdoUWvA7Prd3yR8uyxaXft/?node-id=679%3A44  <= just this one is needed
    if (!loadingTemplates && (refresh || !templateDocs.templatePages)) {
        loadingTemplates = true;
        const options = {
            method: 'POST',
            withCredentials: false,
            headers: {
                'Content-Type': 'application/json',
            },
            responseType: 'arraybuffer', 'decompress': true,
            data: { figmaFileKey: templateFileKey, figmaNodeID: templatePagesNodeID, refresh }
        };
        const response = await axios(`${apiServer}${apiPaths.getFigmaFileJSON}`, options);
        if (response) {
            console.log('  ---> got response getFigmaFileJSON');
            // template/pages loaded, reformat usefully
            const templatePages = {};
            const doc = JSON.parse(new TextDecoder('utf-8').decode(response.data));
            doc.children.find(n => n.id === templatePagesNodeID).children.forEach(p => {
                if (p.children) {
                    templatePages[p.name] = p;
                    p.nodesByName = {};
                    p.children.forEach(n => {
                        p.nodesByName[n.name] = n;
                        n.x = n.absoluteBoundingBox.x - p.absoluteBoundingBox.x;
                        n.y = n.absoluteBoundingBox.y - p.absoluteBoundingBox.y;
                        n.width = n.absoluteBoundingBox.width;
                        n.height = n.absoluteBoundingBox.height;
                    })
                }
            });
            Object.assign(templateDocs, { templatePages });
            loadingTemplates = false;
            console.log('templateDocs', templateDocs);
        }
    } else {
        console.log('===> !!  didnt load templates');
    }
};

// export as downloadable JSON file
export const exportNode = (node) => exporNodeStruct(deflateNodeTree(node));

export const exporNodeStruct = (nodeStruct) => {
    const url = window.URL.createObjectURL(new Blob([JSON.stringify(nodeStruct)]));
    const link = document.createElement('a');
    const node = nodeStruct.nodeMap[nodeStruct.root];
    link.href = url;
    const system = window.location.origin.replace(/http(s)*:\/\//, '').replace(/:\d+/, '');
    link.setAttribute('download', `export-${system}-${node.nodeClass}-${node.number}-${node.title}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const assignLessonToPattern = (lesson, pattern) => {

};